import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    MessageSquareText,
    X,
    Send,
    Sparkles,
    Trash2,
    Bot,
    User,
    Loader2,
    ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const SUGGESTIONS = [
    "J'en suis où sur ce chantier ?",
    "Combien coûte la plomberie ?",
    "Quels sont mes doublons à régler ?",
    "Quels postes sont oubliés et critiques ?",
    "Quel est mon poste le plus en dépassement de budget ?",
    "Quelles entreprises ai-je consultées ?",
];

export default function ChatAssistant({ projectId }) {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const scrollerRef = useRef(null);
    const inputRef = useRef(null);

    const loadHistory = async () => {
        try {
            const data = await api.listChat(projectId);
            setMessages(data);
        } catch (e) {
            console.error("ChatAssistant loadHistory failed:", e);
        } finally {
            setHistoryLoaded(true);
        }
    };

    useEffect(() => {
        if (open && !historyLoaded) loadHistory();
        // eslint-disable-next-line
    }, [open, historyLoaded]);

    useEffect(() => {
        if (scrollerRef.current) {
            scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
        }
    }, [messages, loading]);

    useEffect(() => {
        if (open && inputRef.current) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [open]);

    const send = async (text) => {
        const content = (text ?? input).trim();
        if (!content || loading) return;
        const userMsg = {
            id: `tmp-${Date.now()}`,
            role: "user",
            content,
            created_at: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, userMsg]);
        setInput("");
        setLoading(true);
        try {
            const reply = await api.sendChat(projectId, content);
            // replace tmp user msg with the persisted pair (refresh from server)
            const fresh = await api.listChat(projectId);
            setMessages(fresh);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Erreur de l'assistant IA"
            );
            // remove the optimistic user msg on error
            setMessages((prev) => prev.filter((m) => m.id !== userMsg.id));
        } finally {
            setLoading(false);
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    };

    const clear = async () => {
        if (!window.confirm("Effacer toute la conversation ?")) return;
        await api.clearChat(projectId);
        setMessages([]);
        toast.success("Conversation effacée");
    };

    return (
        <>
            {/* FAB */}
            {!open && (
                <button
                    onClick={() => setOpen(true)}
                    data-testid="chat-fab"
                    className="fixed bottom-24 right-6 z-40 bg-ink text-white shadow-2xl border border-ink hover:bg-accent transition-colors group flex items-center gap-2 px-4 py-3"
                >
                    <Sparkles
                        className="w-4 h-4 group-hover:rotate-12 transition-transform"
                        strokeWidth={1.8}
                    />
                    <span className="font-heading font-bold text-sm uppercase tracking-wider">
                        Assistant IA
                    </span>
                </button>
            )}

            {/* Chat panel */}
            {open && (
                <div
                    data-testid="chat-panel"
                    className="fixed bottom-24 right-6 z-40 w-[420px] max-w-[calc(100vw-32px)] h-[600px] max-h-[calc(100vh-140px)] bg-white border border-ink shadow-2xl flex flex-col"
                >
                    {/* Header */}
                    <div className="px-4 py-3 border-b border-border bg-ink text-white flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 bg-accent flex items-center justify-center">
                                <Sparkles
                                    className="w-3.5 h-3.5"
                                    strokeWidth={2}
                                />
                            </div>
                            <div>
                                <div className="font-heading font-bold text-sm leading-none">
                                    Assistant chantier
                                </div>
                                <div className="text-[10px] uppercase tracking-wider text-white/60 mt-0.5">
                                    Claude Sonnet 4.5
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-0.5">
                            {messages.length > 0 && (
                                <button
                                    onClick={clear}
                                    className="p-1.5 hover:bg-white/10 text-white/70 hover:text-white"
                                    title="Effacer la conversation"
                                    data-testid="chat-clear-btn"
                                >
                                    <Trash2
                                        className="w-3.5 h-3.5"
                                        strokeWidth={1.6}
                                    />
                                </button>
                            )}
                            <button
                                onClick={() => setOpen(false)}
                                className="p-1.5 hover:bg-white/10 text-white"
                                data-testid="chat-close-btn"
                            >
                                <ChevronDown
                                    className="w-4 h-4"
                                    strokeWidth={1.6}
                                />
                            </button>
                        </div>
                    </div>

                    {/* Messages */}
                    <div
                        ref={scrollerRef}
                        className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-[#FAFAFA]"
                    >
                        {messages.length === 0 && !loading && (
                            <div className="text-center py-6">
                                <Bot
                                    className="w-10 h-10 mx-auto text-ink-soft mb-3"
                                    strokeWidth={1.5}
                                />
                                <div className="font-heading text-base font-bold">
                                    Posez votre question
                                </div>
                                <p className="text-xs text-ink-soft mt-1.5 mb-4">
                                    Je connais tous vos devis, doublons,
                                    oublis et budgets de ce chantier.
                                </p>
                                <div className="flex flex-wrap gap-1.5 justify-center">
                                    {SUGGESTIONS.slice(0, 4).map((s) => (
                                        <button
                                            key={s}
                                            onClick={() => send(s)}
                                            data-testid="chat-suggestion"
                                            className="text-[11px] bg-white border border-border hover:border-ink px-2.5 py-1.5 leading-snug text-left transition-colors"
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {messages.map((m) => (
                            <Bubble key={m.id} message={m} />
                        ))}
                        {loading && (
                            <div className="flex items-start gap-2">
                                <div className="w-7 h-7 shrink-0 bg-ink text-white flex items-center justify-center">
                                    <Bot
                                        className="w-3.5 h-3.5"
                                        strokeWidth={1.8}
                                    />
                                </div>
                                <div className="bg-white border border-border px-3 py-2.5 flex items-center gap-2 text-xs text-ink-soft">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    Réflexion en cours…
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Input */}
                    <div className="border-t border-border bg-white p-3">
                        <div className="flex items-center gap-2">
                            <Input
                                ref={inputRef}
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        send();
                                    }
                                }}
                                placeholder="Ex: combien coûte la plomberie ?"
                                disabled={loading}
                                className="rounded-none flex-1 h-10 text-sm"
                                data-testid="chat-input"
                            />
                            <Button
                                onClick={() => send()}
                                disabled={loading || !input.trim()}
                                className="rounded-none bg-accent hover:bg-accent-hover h-10 px-3"
                                data-testid="chat-send"
                            >
                                {loading ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Send className="w-4 h-4" strokeWidth={1.8} />
                                )}
                            </Button>
                        </div>
                        <div className="mt-1.5 text-[10px] text-ink-soft uppercase tracking-wider">
                            Entrée pour envoyer · Maj+Entrée pour saut de ligne
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function Bubble({ message }) {
    const isUser = message.role === "user";
    return (
        <div
            className={`flex items-start gap-2 ${
                isUser ? "flex-row-reverse" : ""
            }`}
            data-testid={`chat-msg-${message.role}`}
        >
            <div
                className={`w-7 h-7 shrink-0 flex items-center justify-center ${
                    isUser ? "bg-accent text-white" : "bg-ink text-white"
                }`}
            >
                {isUser ? (
                    <User className="w-3.5 h-3.5" strokeWidth={1.8} />
                ) : (
                    <Bot className="w-3.5 h-3.5" strokeWidth={1.8} />
                )}
            </div>
            <div
                className={`max-w-[85%] px-3 py-2.5 text-sm leading-relaxed border whitespace-pre-wrap ${
                    isUser
                        ? "bg-accent text-white border-accent"
                        : "bg-white border-border text-ink"
                }`}
            >
                {message.content}
            </div>
        </div>
    );
}
